// Where the athlete is, as this device remembers it (SPEC-27): the tab each
// questionnaire was opened from, so « Sair », ← on its introduction and its
// confirmation lead back there, the assessment just confirmed from Conversa
// so Lena thanks her there (SPEC-28), and the last screen she was on, so the app reopens
// there (the founder's answer 7). UI facts of one device, never sent
// anywhere and never health data: an assessment id and a path.
//
// Storage that throws (a private window, blocked site data) reads as
// unknown, and the app falls back to Conversa.

import { ASSESSMENT_IDS } from "../schema/types";
import type { AssessmentId } from "../schema/types";
import { assessmentPaths, paths } from "./router";
import type { Path } from "./router";

/** The tab a questionnaire was opened from. */
export type Origin = "conversa" | "avaliacoes";

const originKey = (assessmentId: AssessmentId) => `pyf.origin.${assessmentId}`;
const PLACE_KEY = "pyf.lastPlace";

/** The screens the app may reopen on: its three tabs and its questionnaires. */
const PLACES: readonly Path[] = [paths.conversa, paths.assessments, paths.profile, paths.profileEdit, ...Object.values(assessmentPaths)];

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Unavailable storage costs the memory only.
  }
}

/** Opening a questionnaire records its tab; the last opening counts, so resuming from the other tab moves it. */
export function rememberOrigin(assessmentId: AssessmentId, origin: Origin): void {
  write(originKey(assessmentId), origin);
}

/** The tab this questionnaire was opened from, or null when the device does not know (a deep link, a reinstall). */
export function originOf(assessmentId: AssessmentId): Origin | null {
  const origin = read(originKey(assessmentId));
  return origin === "conversa" || origin === "avaliacoes" ? origin : null;
}

/** Where a way out of a questionnaire leads: its tab, Conversa when unknown. */
export const originPath = (origin: Origin | null): Path => (origin === "avaliacoes" ? paths.assessments : paths.conversa);

/** The two ways out at the end of Dados gerais, the tab it was opened from first; Conversa first when unknown. */
export const waysOut = (origin: Origin | null): readonly Path[] =>
  origin === "avaliacoes" ? [paths.assessments, paths.conversa] : [paths.conversa, paths.assessments];

const FINISHED_KEY = "pyf.justFinished";

/** An assessment just confirmed from Conversa, and the lib's name for its ending when it has several. */
export interface Finished {
  readonly assessmentId: AssessmentId;
  readonly closing?: string;
}

/**
 * SPEC-28 (the founder's answer 1): confirming from Conversa returns there,
 * and Conversa opens on Lena's thanks for it, once.
 */
export function rememberFinished(finished: Finished): void {
  write(FINISHED_KEY, JSON.stringify(finished));
}

/** The assessment Conversa thanks her for, or null; it stays until forgetFinished. */
export function justFinished(): Finished | null {
  try {
    const stored: unknown = JSON.parse(read(FINISHED_KEY) ?? "null");
    if (typeof stored !== "object" || stored === null) return null;
    const { assessmentId, closing } = stored as Record<string, unknown>;
    if (!ASSESSMENT_IDS.some((id) => id === assessmentId)) return null;
    return { assessmentId: assessmentId as AssessmentId, ...(typeof closing === "string" && { closing }) };
  } catch {
    return null;
  }
}

/** Once Conversa has shown the thanks: the next visit opens on the proposal alone. */
export function forgetFinished(): void {
  write(FINISHED_KEY, null);
}

export function rememberPlace(path: string): void {
  if (PLACES.some((place) => place === path)) write(PLACE_KEY, path);
}

/** The screen the app was last on, if it is one it reopens on. */
export function lastPlace(): Path | null {
  const path = read(PLACE_KEY);
  return PLACES.find((place) => place === path) ?? null;
}

/** On sign-out: the next athlete on this phone starts from nothing. One origin per assessment, one place, one thanks. */
export function forgetPlaces(): void {
  for (const assessmentId of ASSESSMENT_IDS) write(originKey(assessmentId), null);
  write(PLACE_KEY, null);
  forgetFinished();
}
